/**
 * InspectionCarouselOverlay — fullscreen overlay that shows the inspection
 * issue cards in a carousel (prev/next + pill nav). No header chrome, no
 * DNA bar, no filters. Just the cards.
 *
 * Triggered from the "See inspection details →" link on the HomeDNA card
 * in HomeHQ.
 */
import {
  Box,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  Search,
  TriangleAlert,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import CalculatorSvg from '@/assets/icons/calculator.svg?react';
import ClockSvg from '@/assets/icons/clock.svg?react';
import { COLORS } from '@/components/dashboard/scoring';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { Issue, IssueUrgency, urgencyOrder } from '@/models/Issue';
import { getAverageIssueCost } from '@/utils/issues';

const URGENCY_CHIP_COLORS: Record<IssueUrgency, string> = {
  [IssueUrgency.Critical]: COLORS.critical,
  [IssueUrgency.Urgent]: COLORS.urgent,
  [IssueUrgency.Moderate]: COLORS.moderate,
  [IssueUrgency.Low]: COLORS.low,
  [IssueUrgency.Monitor]: COLORS.monitor,
};

const BIG_TICKET_THRESHOLD = 5000;

const formatCategory = (category: string) =>
  category
    .split(' ')
    .map((word) => (word === 'HVAC' ? 'HVAC' : word.charAt(0) + word.slice(1).toLowerCase()))
    .join(' ');

interface IssueCardProps {
  issue: Issue;
  isActive: boolean;
  isMobile?: boolean;
}

const IssueCard = ({ issue, isActive, isMobile }: IssueCardProps) => {
  const isBigTicket = getAverageIssueCost(issue) >= BIG_TICKET_THRESHOLD;

  const formatCostRange = () => {
    const min = issue.costEstimateMin ?? 0;
    const max = issue.costEstimateMax ?? 0;
    const currency = issue.currencySymbol || '$';
    return `${currency}${min.toLocaleString()} – ${currency}${max.toLocaleString()}`;
  };

  const formatTimeline = () => {
    const minDays = issue.projectTimelineMinDays;
    const maxDays = issue.projectTimelineMaxDays;
    if (!minDays && !maxDays) return null;
    const upper = (maxDays ?? minDays) as number;
    // 1-day-or-less timelines collapse to "≤ 1 Day".
    if (upper <= 1) return '≤ 1 Day';
    if (minDays && maxDays && minDays !== maxDays) return `${minDays}-${maxDays} Days`;
    return `${upper} Days`;
  };

  return (
    <Card
      sx={{
        // a solid card (the app's card material), so the cards stacked behind never read through it
        bgcolor: 'var(--tx-glass-panel)',
        backgroundImage: 'var(--tx-surface-light)',
        borderRadius: '24px',
        // Mobile: taller portrait ratio. Desktop: original.
        minHeight: isMobile ? 500 : 430,
        display: 'flex',
        flexDirection: 'column',
        // a real card's shadow: a tight contact line, a mid shadow, and a long soft fall; the top card sits highest
        boxShadow: isActive
          ? '0 1px 1px rgba(0,0,0,0.35), 0 10px 20px -6px rgba(0,0,0,0.45), 0 40px 80px -24px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)'
          : '0 1px 1px rgba(0,0,0,0.3), 0 8px 16px -6px rgba(0,0,0,0.4), 0 24px 50px -20px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
        transition: 'all 0.3s ease',
      }}>
      <CardContent
        sx={{
          p: '20px',
          pb: '20px',
          '&:last-child': { pb: '20px' },
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}>
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: '10px' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'var(--tx-muted)' }}>
              {formatCategory(issue.category)}
              {isBigTicket && (
                <span style={{ color: COLORS.urgent, fontWeight: 600, marginLeft: 6 }}>
                  &middot;{' '}
                  <TriangleAlert size={12} style={{ verticalAlign: 'middle', marginRight: 2 }} />{' '}
                  Big ticket
                </span>
              )}
            </Typography>
            {/* the app's legend item: a 2px tick in the urgency colour and the label, not a filled pill */}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                fontWeight: 500,
                color: 'var(--tx-text)',
                fontFamily: "'Manrope', sans-serif",
              }}>
              <span
                style={{
                  width: 2,
                  height: 12,
                  borderRadius: 1,
                  background: issue.resolved ? 'var(--tx-lime)' : URGENCY_CHIP_COLORS[issue.urgency],
                }}
              />
              {issue.resolved ? 'Resolved' : issue.urgency}
            </span>
          </Stack>

          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 300,
              letterSpacing: '-0.025em',
              lineHeight: 1.2,
              color: 'var(--tx-text)',
              textWrap: 'balance',
              maxWidth: '20ch',
            }}>
            {issue.title}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }} />

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              color: 'var(--tx-muted)',
              lineHeight: 1.6,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mb: '20px',
            }}>
            {issue.description}
          </Typography>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ pt: '20px', borderTop: '1px solid var(--tx-line)' }}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={isMobile ? '10px' : '30px'}
              alignItems={isMobile ? 'flex-start' : 'center'}>
              <Stack direction="row" spacing={'10px'} alignItems="center">
                <CalculatorSvg
                  style={{ width: 15, height: 15, color: 'var(--tx-muted)' }}
                />
                <Typography sx={{ fontSize: 14, fontWeight: 300, letterSpacing: '-0.01em', color: 'var(--tx-text)' }}>
                  {formatCostRange()}
                </Typography>
              </Stack>
              {formatTimeline() && (
                <Stack direction="row" spacing={'10px'} alignItems="center">
                  <ClockSvg
                    style={{ width: 15, height: 15, color: 'var(--tx-muted)' }}
                  />
                  <Typography sx={{ fontSize: 14, fontWeight: 300, letterSpacing: '-0.01em', color: 'var(--tx-text)' }}>
                    {formatTimeline()}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

// The pager as HomeDNA itself: every finding a thin line (height by cost, urgency colour, Critical → Monitor), the
// current one lit and the rest set back. Click a line, or press and drag along the strip, to move through them.
const FindingStrip = ({
  issues,
  activeIndex,
  onSelect,
  width,
}: {
  issues: Issue[];
  activeIndex: number;
  onSelect: (index: number) => void;
  width: number;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);
  const maxCost = Math.max(1, ...issues.map((i) => getAverageIssueCost(i)));
  const pick = (clientX: number) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r || issues.length === 0) return;
    const t = Math.min(0.9999, Math.max(0, (clientX - r.left) / r.width));
    onSelect(Math.floor(t * issues.length));
  };
  return (
    <div
      ref={ref}
      role="slider"
      aria-label="Findings"
      aria-valuemin={1}
      aria-valuemax={issues.length}
      aria-valuenow={activeIndex + 1}
      onPointerDown={(e) => {
        dragging.current = true;
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        pick(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && pick(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
      style={{
        width,
        maxWidth: '100%',
        height: 44,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        borderBottom: '1px solid var(--tx-line)',
        cursor: 'pointer',
        touchAction: 'none',
        pointerEvents: 'auto',
      }}>
      {issues.map((issue, i) => {
        const on = i === activeIndex;
        return (
          <span
            key={issue.id}
            style={{
              width: on ? 2 : 1,
              minWidth: 0.5,
              flexShrink: 1,
              height: `${on ? 100 : Math.max(18, Math.sqrt(getAverageIssueCost(issue) / maxCost) * 88)}%`,
              borderRadius: '1px 1px 0 0',
              background: on ? 'var(--tx-text)' : URGENCY_CHIP_COLORS[issue.urgency],
              opacity: on ? 1 : 0.4,
              transition: 'height 0.2s ease, opacity 0.2s ease',
            }}
          />
        );
      })}
    </div>
  );
};

export interface InspectionCarouselOverlayProps {
  open: boolean;
  onClose: () => void;
  /** When the overlay opens, jump to the card for this issue id (if present in the sorted list). */
  initialIssueId?: string | null;
}

export const InspectionCarouselOverlay = ({
  open,
  onClose,
  initialIssueId,
}: InspectionCarouselOverlayProps) => {
  const { propertyIssues } = usePropertiesContext();
  const [activeIndex, setActiveIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  // The blur layer fills from the sidebar's right edge to the viewport's
  // right edge (so it extends beneath the chat drawer). The carousel is
  // separately centered over the actual main-content area, which can be
  // narrower (e.g. when the chat drawer is open).
  const [blurLeft, setBlurLeft] = useState<number | null>(null);
  const [contentRect, setContentRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const sidebar = document.getElementById('dashboard-left-sidebar');
      setBlurLeft(sidebar ? sidebar.getBoundingClientRect().right : 0);
      const main = document.getElementById('dashboard-main-content');
      if (main) {
        const r = main.getBoundingClientRect();
        setContentRect({ top: r.top, left: r.left, width: r.width });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    const sidebar = document.getElementById('dashboard-left-sidebar');
    const main = document.getElementById('dashboard-main-content');
    if (sidebar) ro.observe(sidebar);
    if (main) ro.observe(main);
    window.addEventListener('resize', measure);
    // Re-measure during the sidebar's 200ms width transition.
    const transitionTimer = window.setInterval(measure, 50);
    const transitionStop = window.setTimeout(() => window.clearInterval(transitionTimer), 400);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      window.clearInterval(transitionTimer);
      window.clearTimeout(transitionStop);
    };
    // Re-run on isMobile change so observers attach to the fresh layout
    // elements when toggling between mobile/desktop in devtools.
  }, [open, isMobile]);

  // Stable urgency-ordered list of all unresolved issues for the property
  // (used to detect the truly-empty state).
  const allUnresolvedIssues = useMemo(() => {
    const issues = (propertyIssues ?? []).filter((i) => !i.resolved);
    return [...issues].sort((a, b) => urgencyOrder[a.urgency] - urgencyOrder[b.urgency]);
  }, [propertyIssues]);

  // The list shown in the carousel — narrowed by the search input (matches
  // title, category, component, or trade, case-insensitive).
  const sortedIssues = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allUnresolvedIssues;
    return allUnresolvedIssues.filter((i) =>
      [i.title, i.category, i.component, i.componentCategory, i.trade]
        .filter(Boolean)
        .some((s) => (s as string).toLowerCase().includes(q)),
    );
  }, [allUnresolvedIssues, searchQuery]);

  // When the overlay opens, jump to the requested issue (if any), otherwise
  // start from the first card.
  useEffect(() => {
    if (!open) return;
    const idx = initialIssueId
      ? sortedIssues.findIndex((i) => i.id === initialIssueId)
      : -1;
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [open, initialIssueId, sortedIssues]);
  useEffect(() => {
    if (activeIndex >= sortedIssues.length) setActiveIndex(0);
  }, [sortedIssues.length, activeIndex]);

  const goToPrevious = useCallback(() => {
    setActiveIndex((prev) => (prev <= 0 ? sortedIssues.length - 1 : prev - 1));
  }, [sortedIssues.length]);

  const goToNext = useCallback(() => {
    setActiveIndex((prev) => (prev >= sortedIssues.length - 1 ? 0 : prev + 1));
  }, [sortedIssues.length]);

  const goToFirst = useCallback(() => {
    setActiveIndex(0);
  }, []);

  const goToLast = useCallback(() => {
    setActiveIndex(Math.max(0, sortedIssues.length - 1));
  }, [sortedIssues.length]);

  // Keyboard nav while overlay is open.
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      else if (e.key === 'ArrowRight') goToNext();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, goToPrevious, goToNext, onClose]);

  // Touch swipe on mobile.
  const touchStartX = useRef<number | null>(null);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      if (deltaX < -50) goToNext();
      else if (deltaX > 50) goToPrevious();
      touchStartX.current = null;
    },
    [goToNext, goToPrevious],
  );

  if (!open || blurLeft === null || !contentRect) return null;

  return createPortal(
    <>
      {/* Blur layer — extends from sidebar's right edge to viewport right
          (beneath the chat drawer). Mobile uses a much lighter tint so the
          fixed app bar + property selector (glass, semi-transparent) above
          don't get darkened to the point of disappearing. */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: blurLeft,
          right: 0,
          zIndex: 1100,
          // cards laid on the table: the page stays in view under them, softened (6px blur), no tint
          backgroundColor: 'transparent',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          animation: 'hxScrimIn 0.28s ease-out',
          '@keyframes hxScrimIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        }}
      />

      {/* Foreground — carousel + nav, centered over the actual main-content
          area (excludes the chat drawer, so cards stay visually centered
          within what the user sees). */}
      <Box
        sx={{
          position: 'fixed',
          top: contentRect.top,
          left: contentRect.left,
          width: contentRect.width,
          bottom: 0,
          zIndex: 1101,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          // Mobile: clear the fixed app bar (~80) + property selector (~70)
          // so the carousel doesn't sit underneath them.
          ...(isMobile && { pt: '150px' }),
        }}>
        {allUnresolvedIssues.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ py: 8, pointerEvents: 'auto' }}>
            <Typography variant="h6" color="var(--tx-muted)" fontWeight={500}>
              No issues found
            </Typography>
            <Typography variant="body2" color="#404040" sx={{ mt: 1 }}>
              No inspection issues found for this property.
            </Typography>
          </Stack>
        ) : (
          <>
            <Box
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              sx={{
                display: 'block',
                position: 'relative',
                width: '100%',
                minHeight: 500,
                pointerEvents: 'auto',
                // Mobile: tighter min-height matching the card so the gap to
                // the pagination Stack below stays at exactly 20px.
                ...(isMobile && { minHeight: 500 }),
              }}>
              {sortedIssues.map((issue, index) => {
                const offset = index - activeIndex;
                // Mobile: render only the active card. Touch swipe handles
                // navigation. Desktop: keep the 3-card stack (active + ±1).
                if (isMobile ? offset !== 0 : Math.abs(offset) > 1) return null;

                const isActive = offset === 0;
                // a fanned hand of cards: the neighbours tip out and drop a little behind the one in front
                const scale = isActive ? 1 : 0.9;
                const cardWidth = isMobile ? 290 : 535;
                const translateX = isMobile ? 0 : offset * 330;
                const translateY = isActive ? 0 : 26;
                const rotate = isActive ? 0 : offset * 6;
                const zIndex = isActive ? 5 : 3;
                const opacity = 1;

                return (
                  <Box
                    key={issue.id}
                    onClick={() => !isActive && setActiveIndex(index)}
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      width: cardWidth,
                      transition: 'all 0.35s cubic-bezier(0.25, 0.1, 0.25, 1)',
                      transform: `translateX(calc(-50% + ${translateX}px)) translateY(${translateY}px) rotate(${rotate}deg) scale(${scale})`,
                      transformOrigin: '50% 100%',
                      zIndex,
                      opacity,
                      // the cards behind read darker, as a card under another one would
                      filter: isActive ? 'none' : 'brightness(0.72)',
                      cursor: isActive ? 'default' : 'pointer',
                    }}>
                    <IssueCard issue={issue} isActive={isActive} isMobile={isMobile} />
                  </Box>
                );
              })}
            </Box>

            <Stack
              direction="column"
              alignItems="center"
              spacing={0}
              sx={{
                // the controls on a card of their own, laid on the table like the findings
                mt: '24px',
                p: '18px 22px 14px',
                borderRadius: '20px',
                backgroundColor: 'var(--tx-dialog)',
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: '0 1px 1px rgba(0,0,0,0.3), 0 10px 24px -8px rgba(0,0,0,0.45), 0 30px 60px -24px rgba(0,0,0,0.6)',
                pointerEvents: 'auto',
                ...(isMobile && { mt: '20px' }),
              }}>
              {sortedIssues.length > 1 && (
                <FindingStrip
                  issues={sortedIssues}
                  activeIndex={activeIndex}
                  onSelect={setActiveIndex}
                  width={isMobile ? 290 : 535}
                />
              )}
              <TextField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search remarks…"
                size="small"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} color="#bbb7af" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: isMobile ? 290 : 375,
                  // the app's field: a tight radius and a tonal fill, no outline and no pill
                  mt: '16px',
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: 'var(--tx-fill)',
                    color: 'var(--tx-text)',
                    fontSize: 13,
                    '& fieldset': { border: 0 },
                    '&.Mui-focused': { bgcolor: 'var(--tx-fill-2)' },
                  },
                  '& input::placeholder': { color: 'var(--tx-faint)', opacity: 1 },
                }}
              />
              {/* Pagination row — always rendered with a fixed footprint so
                  typing in the search box doesn't shift surrounding layout.
                  Buttons disable when there's nowhere to go; the counter
                  swaps to a no-match label inline (same width slot). */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                // the icon buttons carry their own padding, so 8px here reads as the same 16 above the field
                sx={{ height: 36, mt: '8px' }}>
                <IconButton
                  onClick={goToFirst}
                  disabled={sortedIssues.length <= 1 || activeIndex === 0}
                  aria-label="Go to first"
                  size="small"
                  sx={{
                    color: 'var(--tx-text)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                    '&.Mui-disabled': { color: 'rgba(243,243,243,0.3)' },
                  }}>
                  <ChevronFirst size={20} />
                </IconButton>
                <IconButton
                  onClick={goToPrevious}
                  disabled={sortedIssues.length <= 1}
                  aria-label="Previous"
                  size="small"
                  sx={{
                    color: 'var(--tx-text)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                    '&.Mui-disabled': { color: 'rgba(243,243,243,0.3)' },
                  }}>
                  <ChevronLeft size={20} />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: 'var(--tx-muted)',
                    width: 120,
                    textAlign: 'center',
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  {sortedIssues.length === 0
                    ? 'No matches'
                    : `${activeIndex + 1} / ${sortedIssues.length}`}
                </Typography>
                <IconButton
                  onClick={goToNext}
                  disabled={sortedIssues.length <= 1}
                  aria-label="Next"
                  size="small"
                  sx={{
                    color: 'var(--tx-text)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                    '&.Mui-disabled': { color: 'rgba(243,243,243,0.3)' },
                  }}>
                  <ChevronRight size={20} />
                </IconButton>
                <IconButton
                  onClick={goToLast}
                  disabled={
                    sortedIssues.length <= 1 ||
                    activeIndex === sortedIssues.length - 1
                  }
                  aria-label="Go to last"
                  size="small"
                  sx={{
                    color: 'var(--tx-text)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                    '&.Mui-disabled': { color: 'rgba(243,243,243,0.3)' },
                  }}>
                  <ChevronLast size={20} />
                </IconButton>
              </Stack>
            </Stack>
          </>
        )}
      </Box>
    </>,
    document.body,
  );
};
